import { useEffect, useMemo, useState } from 'react';
import { apiUrl } from '../../shared/api/apiConfig';
import './TpnVehiclesPage.css';

function formatRetrievedAt(value) {
  if (!value) {
    return 'Not loaded yet';
  }

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
}

export default function TpnVehiclesPage() {
  const [source, setSource] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [loadState, setLoadState] = useState({ type: 'idle', message: '' });

  const activeCount = useMemo(
    () => vehicles.filter((vehicle) => vehicle.isActive).length,
    [vehicles]
  );

  async function loadVehicles() {
    setLoadState({ type: 'pending', message: 'Loading TPN vehicles...' });

    try {
      const response = await fetch(apiUrl('/tpnvehicles'));
      const payload = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(payload?.message || 'Failed to load TPN vehicles.');
      }

      setSource({
        host: payload?.sourceHost ?? 'TPN API',
        retrievedAt: payload?.retrievedAt ?? null,
      });
      setVehicles(Array.isArray(payload?.vehicles) ? payload.vehicles : []);
      setLoadState({ type: 'success', message: '' });
    } catch (error) {
      setLoadState({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to load TPN vehicles.',
      });
    }
  }

  useEffect(() => {
    loadVehicles();
  }, []);

  const isLoading = loadState.type === 'pending';

  return (
    <main className="tpn-vehicles-page">
      <section className="tpn-vehicles-hero">
        <div>
          <p className="tpn-vehicles-eyebrow">TPN ERP vehicle source</p>
          <h1>Vehicle list from dev_tpn</h1>
          <p className="tpn-vehicles-subtitle">
            Calendar is calling the dev_tpn API from the backend, authenticating with the login API,
            and showing the returned fleet here.
          </p>
        </div>
        <div className="tpn-vehicles-summary">
          <div className="tpn-vehicles-badge">
            <strong>{vehicles.length}</strong>
            <span>Vehicles</span>
          </div>
          <div className="tpn-vehicles-badge">
            <strong>{activeCount}</strong>
            <span>Active</span>
          </div>
        </div>
      </section>

      <section className="tpn-vehicles-toolbar">
        <div>
          <span className="tpn-vehicles-source-label">Source</span>
          <strong>{source?.host ?? 'Waiting for connection'}</strong>
          <span>{formatRetrievedAt(source?.retrievedAt)}</span>
        </div>
        <button type="button" onClick={loadVehicles} disabled={isLoading}>
          {isLoading ? 'Refreshing...' : 'Refresh'}
        </button>
      </section>

      {loadState.type === 'error' && (
        <section className="tpn-vehicles-alert" role="alert">
          {loadState.message}
        </section>
      )}

      <section className="tpn-vehicles-card">
        <div className="tpn-vehicles-table-wrap">
          <table className="tpn-vehicles-table">
            <thead>
              <tr>
                <th>Plate</th>
                <th>Model</th>
                <th>Vehicle type ID</th>
                <th>Capacity</th>
                <th>Driver</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={6} className="tpn-vehicles-empty">
                    Loading vehicles...
                  </td>
                </tr>
              )}

              {!isLoading && vehicles.map((vehicle) => (
                <tr key={vehicle.id}>
                  <td className="tpn-vehicles-plate">{vehicle.plateNumber}</td>
                  <td>{vehicle.model}</td>
                  <td className="tpn-vehicles-mono">{vehicle.vehicleTypeId}</td>
                  <td>{vehicle.loadingCapacity}</td>
                  <td>{vehicle.defaultDriver || '-'}</td>
                  <td>
                    <span className={`tpn-vehicles-status ${vehicle.isActive ? 'active' : 'inactive'}`}>
                      {vehicle.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                </tr>
              ))}

              {!isLoading && vehicles.length === 0 && (
                <tr>
                  <td colSpan={6} className="tpn-vehicles-empty">
                    No vehicles returned from dev_tpn.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </main>
  );
}
